import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { useFormik, FormikProvider, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as bie from "react-bootstrap";
import { CommonAxiosGet, CommonAxiosPost } from "../CommonUtils/CommonAxios";
import { config } from "../CommonUtils/CommonURLS";
import { failureAlert, successAlert2, successAlertRefresh } from "../CommonUtils/SweetAlerts";
import Swal from "sweetalert2";
import { ERROR_MSG } from "../CommonUtils/contextVariables";
import allowNumbersOnly, { allowAlphabetAndSpacesOnly } from "../CommonUtils/CommonValidations";
import { emailValidation, mobileValidation, pincodeValidation } from "../CommonYupValidations";
import { FaEye } from "react-icons/fa";
import { ImagePdfBucket } from "../CommonUtils/ImagesBase64";
import { viewImage } from "../CommonUtils/viewImage";

export default function InstitutionInfo({ goNext, paymentDone }) {
    const validationSchema = Yup.object({
        placeTown: Yup.string().required(ERROR_MSG),
        instAddress: Yup.string().required(ERROR_MSG),
        mobileNo: mobileValidation,
        principleMobile: mobileValidation,
        email: emailValidation,
        instManagementType: Yup.string().required(ERROR_MSG),
        societyRegNo: Yup.string().required(ERROR_MSG),
        secretryMobile: mobileValidation,
        secretryEmail: emailValidation,
        minorStatus: Yup.string().required(ERROR_MSG),
        totalFacultyCount: Yup.string().required(ERROR_MSG),
        naacAccredited: Yup.string().required("Required"),

        programmesOffered: Yup.string().required("Required"),
        scholarshipsList: Yup.string().required("Required"),
        studentResults: Yup.string().required("Required"),
        extensionActivities: Yup.string().required("Required"),
        collegeWebsite: Yup.string().required("Required"),
        principleEmail: emailValidation,
        principleName: Yup.string().required(ERROR_MSG),

        // ================= LATEST NAAC =================
        naacStatus: Yup.string().when("naacAccredited", {
            is: "Yes",
            then: (schema) => schema.required("Grade is required"),
            otherwise: (schema) => schema.notRequired(),
        }),

        latestNaacCgpa: Yup.number()
            .typeError("CGPA must be a number")
            .when("naacAccredited", {
                is: "Yes",
                then: (schema) => schema.required("CGPA is required").min(0).max(4),
                otherwise: (schema) => schema.notRequired(),
            }),

        latestNaacFromDate: Yup.date().when("naacAccredited", {
            is: "Yes",
            then: (schema) => schema.required("From Date required"),
            otherwise: (schema) => schema.notRequired(),
        }),

        latestNaacUptoDate: Yup.date().when("naacAccredited", {
            is: "Yes",
            then: (schema) => schema.required("To Date required").min(Yup.ref("latestNaacFromDate"), "Invalid date"),
            otherwise: (schema) => schema.notRequired(),
        }),

        latestNaacCerti: Yup.mixed().when("naacAccredited", {
            is: "Yes",
            then: (schema) => schema.required("Certificate required"),
            otherwise: (schema) => schema.notRequired(),
        }),

        // ================= PREVIOUS =================

        hasPreviousNaac: Yup.string().when("naacAccredited", {
            is: (val) => val === "Yes",
            then: (schema) => schema.required("Required"),
            otherwise: (schema) => schema.notRequired(),
        }),
        naacCycle: Yup.string().when(["naacAccredited", "hasPreviousNaac"], {
            is: (naacAccredited, hasPreviousNaac) => naacAccredited === "Yes" && hasPreviousNaac === "Yes",
            then: (schema) => schema.required("NAAC Cycle is required"),
            otherwise: (schema) => schema.notRequired(),
        }),
        naacDetails: Yup.array().when(["naacAccredited", "hasPreviousNaac", "naacCycle"], ([naacAccredited, hasPreviousNaac, naacCycle], schema) => {
            // Apply only when NAAC Yes + Previous Yes
            if (naacAccredited === "Yes" && hasPreviousNaac === "Yes") {
                return (
                    schema
                        .of(
                            Yup.object().shape({
                                naacStatus: Yup.string().required("Grade is required"),
                                naacCgpa: Yup.number().typeError("Must be number").required("CGPA required").min(1, "Min 1").max(4, "Max 4"),
                                naacFromDate: Yup.date().required("From Date required"),
                                naacUptoDate: Yup.date().required("To Date required").min(Yup.ref("naacFromDate"), "To Date must be after From Date"),
                                naacAccredCerti: Yup.mixed().required("Certificate required"),
                            }),
                        )
                        // ✅ Cycle count validation
                        .test("length-check", "NAAC details must match selected cycle", function (value) {
                            if (!naacCycle) return true;
                            return value && value.length === parseInt(naacCycle);
                        })
                );
            }
            return schema.notRequired();
        }),
    });

    const InstitutionFormik = useFormik({
        initialValues: {
            placeTown: "",
            aisheCode: "",
            instName: "",
            instAddress: "",
            district: "",
            mandal: "",
            pincode: "",
            stdCode: "",
            telephoneNo: "",
            mobileNo: "",
            email: "",
            yearOfEstablishment: "",
            instManagementType: "",
            instType: "",
            societyName: "",
            societyRegNo: "",
            secretryName: "",
            secretryMobile: "",
            secretryEmail: "",
            admitted: "",
            UnivRegion: "",
            minorStatus: "",

            totalIntakeStudent: "",
            totalFacultyCount: "",
            compoundRation: "",
            ubCode: "",
            Univname: "",
            getUGCourses: "",
            collegeWebsite: "",
            principleName: "",
            principleMobile: "",
            principleEmail: "",

            naacAccredited: "",
            naacStatus: "",
            latestNaacFromDate: "",
            latestNaacUptoDate: "",
            latestNaacCerti: "",
            latestNaacCgpa: "",

            hasPreviousNaac: "",

            naacCycle: "",
            naacDetails: [],
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: (val) => {
            Swal.fire({
                text: "Are you sure you want to submit?",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, submit it!",
                cancelButtonText: "No, cancel!",
                backdrop: false,
            }).then((result) => {
                if (result.isConfirmed) {
                    let url = config.url.FAC_URL + `saveInstDetails  `;
                    CommonAxiosPost(url, val).then((res) => {
                        if (res?.data?.scode === "01") {
                            successAlert2(res?.data?.sdesc);
                            GetCollegeData();
                            goNext();
                        } else if (res?.data?.scode === "02") {
                            Swal.fire(res?.data?.sdesc, "", "error").then((res) => { });
                        } else {
                            failureAlert("Failed to submit");
                        }
                    });
                }
            });
        },
    });

    const [distlist, setDistlist] = useState([]);

    function GetDisData() {
        CommonAxiosGet(`${config.url.FAC_URL}getdistricts`).then((res) => {
            setDistlist(res?.data?.scode === "01" ? res.data.districtmst || [] : []);
        });
    }

    const [mdllist, setMdltlist] = useState([]);

    function GetMandlData(distcode) {
        CommonAxiosGet(`${config.url.FAC_URL}getmandals/${distcode}`).then((res) => {
            setMdltlist(res?.data?.scode === "01" ? res.data.mandalmst || [] : []);
        });
    }

    const userId = sessionStorage.getItem("user_id");

    const [clgdata, setClgdata] = useState([]);
    function GetCollegeData() {
        CommonAxiosGet(`${config.url.FAC_URL}getInstitutionPolyDetails`).then((res) => {
            const data = res?.data?.data?.[0] || {};
            //   console.log("year_of_establishmentyear_of_establishment", data?.year_of_establishment);
            setClgdata(data);
            const finalIntake = res?.data?.intake_status === true ? (res?.data?.intake?.[0]?.final_intake ?? "") : "";
            InstitutionFormik?.setValues({
                placeTown: data?.place_town || "",
                instName: data?.inst_name || "",
                instAddress: data?.inst_address || "",
                district: data?.district || "",
                mandal: data?.mandal || "",
                pincode: data?.pincode || "",
                stdCode: data?.std_code || "",
                telephoneNo: data?.telephone_no || "",
                mobileNo: data?.mobile_no || "",
                email: data?.email || "",
                admitted: data?.no_of_students_admitted || "-",
                yearOfEstablishment: data?.year_of_establishment != null ? data.year_of_establishment.toString() : "",
                instManagementType: data?.inst_management_tye || "",
                instType: data?.inst_type?.toString() || "",
                societyName: data?.society_name || "",
                societyRegNo: data?.society_reg_no || "",

                secretryName: data?.secretry_name || "",
                secretryMobile: data?.secretry_mobile || "",
                secretryEmail: data?.secretry_email || "",

                univRegion: data?.ub_name,
                minorStatus: data?.minor_status || "",

                totalIntakeStudent: finalIntake || "",
                totalFacultyCount: data?.total_faculty_count || "",
                compoundRation: data?.compound_ration || "",
                Univname: data?.ub_name || "",
                aisheCode: data?.aishe_code,
                ubCode: data?.ub_code || "",

                programmesOffered: data?.programmes_offered || "",
                scholarshipsList: data?.scholarships_list || "",
                studentResults: data?.student_results || "",
                extensionActivities: data?.extension_activities || "",
                collegeWebsite: data?.college_website || "",
                principleName: data?.principle_name || "",
                principleMobile: data?.principle_mobile || "",
                principleEmail: data?.principle_email || "",
            });

            // ================= NAAC LATEST =================

            const naacMain = res?.data?.data?.[0] || {};
            const naacAccredited = naacMain?.naac_accrediated || "";
            InstitutionFormik.setFieldValue("naacAccredited", naacAccredited);

            // ✅ Only set Latest NAAC if Accredited = Yes
            if (naacAccredited === "Yes") {
                InstitutionFormik.setFieldValue("naacStatus", naacMain?.naac_status || "");
                InstitutionFormik.setFieldValue("latestNaacCgpa", naacMain?.latest_naac_cgpa ? String(naacMain?.latest_naac_cgpa) : "");
                InstitutionFormik.setFieldValue("latestNaacFromDate", naacMain?.latest_naac_from_date || "");
                InstitutionFormik.setFieldValue("latestNaacUptoDate", naacMain?.latest_naac_upto_date || "");
                InstitutionFormik.setFieldValue("hasPreviousNaac", naacMain?.has_previous_naac || "");

                // Certificate
                if (naacMain?.latest_naac_certi) {
                    InstitutionFormik.setFieldValue("latestNaacCerti", naacMain.latest_naac_certi);
                }
            } else {
                // ❗ Clear all NAAC fields
                InstitutionFormik.setFieldValue("naacStatus", "");
                InstitutionFormik.setFieldValue("latestNaacCgpa", "");
                InstitutionFormik.setFieldValue("latestNaacFromDate", "");
                InstitutionFormik.setFieldValue("latestNaacUptoDate", "");
                InstitutionFormik.setFieldValue("latestNaacCerti", "");
                InstitutionFormik.setFieldValue("hasPreviousNaac", "");
                InstitutionFormik.setFieldValue("naacCycle", "");
                InstitutionFormik.setFieldValue("naacDetails", []);
            }

            //   if (dd?.latest_naac_certi !== null && dd?.latest_naac_certi !== undefined && dd?.latest_naac_certi !== "") {
            //     formik?.setFieldValue("latestNaacCerti", dd?.latest_naac_certi);
            //   }
            //   const naacDtls = res?.data?.naacDtls;

            // ================= PREVIOUS NAAC =================

            const naacDtls = res?.data?.naacdtls || [];

            const hasPreviousNaac = naacMain?.has_previous_naac || "";

            if (naacAccredited === "Yes" && hasPreviousNaac === "Yes" && Array.isArray(naacDtls) && naacDtls.length > 0) {
                // Set cycle
                InstitutionFormik.setFieldValue("naacCycle", naacDtls.length.toString());

                // Set details array
                InstitutionFormik.setFieldValue(
                    "naacDetails",
                    naacDtls.map((item) => ({
                        naacStatus: item?.naac_status || "",
                        naacCgpa: item?.naac_cgpa ? String(item?.naac_cgpa) : "",
                        naacFromDate: item?.naac_from_date || "",
                        naacUptoDate: item?.naac_upto_date || "",
                        naacAccredCerti: item?.naac_accred_certi || "",
                    })),
                );
            } else {
                // Clear previous NAAC fields
                InstitutionFormik.setFieldValue("naacCycle", "");
                InstitutionFormik.setFieldValue("naacDetails", []);
            }

            if (data?.district) {
                GetMandlData(parseInt(data.district, 10));
            }
        });
    }
    useEffect(() => {
        GetDisData();
        GetCollegeData();
    }, []);
    const calcSFR = (s, f) => {
        const intake = Number(s) || 0;
        const faculty = Number(f) || 0
        console.log("dddddd", intake, faculty);

        if (faculty > 0) {
            const ratio = (intake / faculty).toFixed(1) + " : 1";
            InstitutionFormik?.setFieldValue("compoundRation", ratio);
        } else {
            InstitutionFormik?.setFieldValue("compoundRation", "");
        }
    };
    return (
        <>
            <div className="faculty-form-wrapper">
                <div className="faculty-card">
                    <FormikProvider value={InstitutionFormik}>
                        <Form className="form_adjust" onSubmit={InstitutionFormik.handleSubmit} onChange={InstitutionFormik.handleChange}>
                            {/* ================= PERSONAL DETAILS ================= */}
                            <h5 className="mb-3 innerheader-new">
                                <span>Institution Details</span>
                            </h5>
                            <bie.Row>
                                <bie.Col xs={12} sm={12} md={12} lg={3} xl={3} xxl={3} className="mb-2">
                                    <label>University Name</label>
                                    <Field
                                        type="text"
                                        name="Univname"
                                        className="form-control"
                                        // maxLength={50}
                                        // onKeyPress={allowAlphabetAndSpacesOnly}
                                        disabled
                                    />
                                    {/* <ErrorMessage name="Univname" component="div" className="text-danger" /> */}
                                </bie.Col>

                                <bie.Col xs={12} sm={12} md={12} lg={3} xl={3} xxl={3} className="mb-2">
                                    <label>Name of Institution</label>
                                    <Field type="text" name="instName" className="form-control" maxLength={50} onKeyPress={allowAlphabetAndSpacesOnly} disabled />
                                    <ErrorMessage name="instName" component="div" className="text-danger" />
                                </bie.Col>
                                <bie.Col xs={12} sm={12} md={12} lg={3} xl={3} xxl={3} className="mb-2">
                                    <label>Institute Code</label>
                                    <Field type="text" name="collCode" className="form-control" value={userId} disabled />
                                </bie.Col>

                                <bie.Col xs={12} sm={12} md={12} lg={3} xl={3} xxl={3} className="mb-2">
                                    <label>AISHE Code</label>
                                    <Field
                                        type="text"
                                        name="aisheCode"
                                        className="form-control"
                                    // maxLength={ }
                                    />
                                    <ErrorMessage name="aisheCode" component="div" className="text-danger" />
                                </bie.Col>
                                <bie.Col xs={12} sm={12} md={12} lg={3} xl={3} xxl={3} className="mb-2">
                                    <label>Place / Town</label>
                                    <Field type="text" name="placeTown" className="form-control" maxLength={60} onKeyPress={allowAlphabetAndSpacesOnly} disabled />
                                    <ErrorMessage name="placeTown" component="div" className="text-danger" />
                                </bie.Col>

                                <bie.Col xs={12} sm={12} md={12} lg={3} xl={3} xxl={3} className="mb-2">
                                    <label>Complete Postal Address with PIN Code</label>
                                    <Field
                                        as="textarea"
                                        name="instAddress"
                                        className="form-control"
                                        // maxLength={90}
                                        // onKeyPress={allowNumbersOnly}
                                        disabled
                                    />
                                    <ErrorMessage name="instAddress" component="div" className="text-danger" />
                                </bie.Col>

                                <bie.Col xs={12} sm={12} md={12} lg={3} xl={3} xxl={3} className="mb-2">
                                    <label> District</label>
                                    <Field
                                        as="select"
                                        name="district"
                                        className="form-control"
                                        disabled
                                        onChange={(e) => {
                                            GetMandlData(e?.target?.value);
                                        }}
                                    >
                                        <option value="">---select---</option>
                                        {distlist?.map((d) => (
                                            <option key={d.dist_code} value={d.dist_code}>
                                                {d.dist_name}
                                            </option>
                                        ))}
                                    </Field>
                                    <ErrorMessage name="district" component="div" className="text-danger" />
                                </bie.Col>
                                <bie.Col xs={12} sm={12} md={12} lg={3} xl={3} xxl={3} className="mb-2">
                                    <label> Mandal</label>
                                    <Field as="select" name="mandal" className="form-control" disabled>
                                        <option value="">---select---</option>
                                        {mdllist?.map((d) => (
                                            <option key={d.mandal_code} value={d.mandal_code}>
                                                {d.mandal_name}
                                            </option>
                                        ))}
                                    </Field>
                                    <ErrorMessage name="mandal" component="div" className="text-danger" />
                                </bie.Col>

                                <bie.Col xs={12} sm={12} md={12} lg={3} xl={3} xxl={3} className="mb-2">
                                    <label>PIN Code </label>
                                    <Field type="text" name="pincode" className="form-control" maxLength={6} onKeyPress={allowNumbersOnly} disabled />
                                    <ErrorMessage name="pincode" component="div" className="text-danger" />
                                </bie.Col>

                                <bie.Col xs={12} sm={12} md={12} lg={3} xl={3} xxl={3} className="mb-2">
                                    <label>STD Code </label>
                                    <Field type="text" name="stdCode" className="form-control" maxLength={6} onKeyPress={allowNumbersOnly} />
                                    <ErrorMessage name="stdCode" component="div" className="text-danger extra-small mt-1" />
                                </bie.Col>

                                <bie.Col xs={12} sm={12} md={12} lg={3} xl={3} xxl={3} className="mb-2">
                                    <label>Telephone No </label>
                                    <Field type="text" name="telephoneNo" className="form-control" maxLength={10} />
                                    <ErrorMessage name="telephoneNo" component="div" className="text-danger" />
                                </bie.Col>

                                <bie.Col xs={12} sm={12} md={12} lg={3} xl={3} xxl={3} className="mb-2">
                                    <label>Mobile No </label>
                                    <Field type="text" name="mobileNo" className="form-control" maxLength={10} onKeyPress={allowNumbersOnly} />
                                    <ErrorMessage name="mobileNo" component="div" className="text-danger" />
                                </bie.Col>

                                <bie.Col xs={12} sm={12} md={12} lg={3} xl={3} xxl={3} className="mb-2">
                                    <label>Email ID</label>
                                    <Field type="email" name="email" className="form-control" />
                                    <ErrorMessage name="email" component="div" className="text-danger" />
                                </bie.Col>

                                <bie.Col md={3}>
                                    <label>Year of Establishment</label>
                                    {/* <Field name="yearOfEstablishment" disabled className="form-control"> */}
                                    <Field type="text" name="yearOfEstablishment" disabled className="form-control">
                                        {/* <option value="">Select Year</option> */}

                                        {/* {[...Array(new Date().getFullYear() - 1947 + 1).keys()]
                                            .map((i) => {
                                                const year = 1947 + i;
                                                return (
                                                    <option key={year} value={year}>
                                                        {year}
                                                    </option>
                                                );
                                            })} */}
                                    </Field>
                                </bie.Col>
                                <bie.Col xs={12} sm={12} md={12} lg={3} xl={3} xxl={3} className="mb-2">
                                    <label>Institute Management Type </label>
                                    <Field as="select" name="instManagementType" className="form-control" disabled>
                                        <option value="">Select</option>
                                        <option value="1"> Government</option>
                                        {/* <option value="6">Autonomous</option> */}
                                        <option value="2">Aided</option>
                                        <option value="3">Private-Unaided</option>
                                    </Field>
                                    <ErrorMessage name="instManagementType" component="div" className="text-danger" />
                                </bie.Col>

                                <bie.Col xs={12} sm={12} md={12} lg={3} xl={3} xxl={3} className="mb-2">
                                    <label>University/Region </label>
                                    <Field type="text" name="UnivRegion" className="form-control" disabled value={clgdata?.ub_name}></Field>
                                </bie.Col>

                                <bie.Col xs={12} sm={12} md={12} lg={3} xl={3} xxl={3} className="mb-2">
                                    <label>Institute Type </label>
                                    <div className="d-flex gap-3">
                                        <label>
                                            <Field type="radio" name="instType" value="Women" className="form-check-input me-1" disabled />
                                            Women
                                        </label>

                                        <label>
                                            <Field type="radio" name="instType" value="Co_Education" className="form-check-input me-1" disabled />
                                            Co-Education
                                        </label>
                                    </div>
                                </bie.Col>

                                <bie.Col xs={12} sm={12} md={12} lg={3} xl={3} xxl={3} className="mb-2">
                                    <label>Principal Name</label>
                                    <Field type="text" name="principleName" className="form-control" onKeyPress={allowAlphabetAndSpacesOnly} maxLength={50} />
                                    <ErrorMessage name="principleName" component="small" className="text-danger" />
                                </bie.Col>

                                <bie.Col xs={12} sm={12} md={12} lg={3} xl={3} xxl={3} className="mb-2">
                                    <label>Principal Mobile </label>
                                    <Field type="text" name="principleMobile" className="form-control" maxLength={10} onKeyPress={allowNumbersOnly} />
                                    <ErrorMessage name="principleMobile" component="small" className="text-danger" />

                                    {/* </Field> */}
                                </bie.Col>

                                <bie.Col xs={12} sm={12} md={12} lg={3} xl={3} xxl={3} className="mb-2">
                                    <label>Principal Email </label>
                                    <Field type="text" name="principleEmail" className="form-control" />
                                    <ErrorMessage name="principleEmail" component="small" className="text-danger" />
                                </bie.Col>
                                <bie.Col md={3}>
                                    <label>Total Approved Intake</label>
                                    <Field
                                        name="totalIntakeStudent"
                                        className="form-control"
                                        disabled
                                    //  maxLength={9} onKeyPress={allowNumbersOnly}
                                    // onChange={(e) => { calcSFR(e.target.value, InstitutionFormik?.values?.totalFacultyCount) }}
                                    />
                                    {/* <ErrorMessage name="totalIntakeStudent" component="div" className="text-danger" /> */}
                                </bie.Col>

                                <bie.Col md={3}>
                                    <label>No of students admitted (S)</label>
                                    <Field
                                        name="admitted"
                                        className="form-control"
                                        maxLength={6}
                                        onKeyPress={allowNumbersOnly}
                                        onKeyUp={(e) => {
                                            InstitutionFormik.handleChange(e);
                                            calcSFR(e.target.value, InstitutionFormik.values.totalFacultyCount);
                                        }}
                                    />
                                </bie.Col>

                                <bie.Col md={3}>
                                    <label>Total Faculty Count (F)</label>
                                    <Field
                                        name="totalFacultyCount"
                                        className="form-control"
                                        maxLength={6}
                                        onKeyPress={allowNumbersOnly}
                                        onKeyUp={(e) => {
                                            InstitutionFormik.handleChange(e);
                                            calcSFR(InstitutionFormik.values.admitted, e.target.value);
                                        }}
                                    />

                                    <ErrorMessage name="totalFacultyCount" component="div" className="text-danger" />
                                </bie.Col>

                                <bie.Col md={3}>
                                    <label>Computed SFR (S : F)</label>
                                    <Field name="compoundRation" className="form-control" readOnly />
                                </bie.Col>
                            </bie.Row>

                            {/* Programmes offered */}

                            <h4 className="mb-3 innerheader-new pt-3">
                                <span> College Website Details</span>
                            </h4>
                            <bie.Row>
                                <bie.Col xs={12} md={4} lg={3} className="mb-3">
                                    <label>College Website URL:</label>
                                    <div className="d-flex gap-3">
                                        <Field type="text" name="collegeWebsite" className="form-control" maxLength={50} />
                                    </div>
                                    <ErrorMessage name="collegeWebsite" component="div" className="text-danger" />
                                </bie.Col>
                                <bie.Col xs={12} md={4} lg={3} className="mb-3">
                                    <label>Programmes offered along with intake and admitted students available in website</label>
                                    <div className="d-flex gap-3">
                                        <label>
                                            <Field type="radio" name="programmesOffered" value="Yes" className="form-check-input me-1" />
                                            Yes
                                        </label>
                                        <label>
                                            <Field type="radio" name="programmesOffered" value="No" className="form-check-input me-1" />
                                            No
                                        </label>
                                    </div>
                                    <ErrorMessage name="programmesOffered" component="div" className="text-danger" />
                                </bie.Col>

                                {/* Scholarships */}
                                <bie.Col xs={12} md={4} lg={3} className="mb-3">
                                    <label>List of students receiving scholarships available in website</label>
                                    <div className="d-flex gap-3">
                                        <label>
                                            <Field type="radio" name="scholarshipsList" value="Yes" className="form-check-input me-1" />
                                            Yes
                                        </label>
                                        <label>
                                            <Field type="radio" name="scholarshipsList" value="No" className="form-check-input me-1" />
                                            No
                                        </label>
                                    </div>
                                    <ErrorMessage name="scholarshipsList" component="div" className="text-danger" />
                                </bie.Col>

                                {/* Results */}
                                <bie.Col xs={12} md={4} lg={3} className="mb-3">
                                    <label>Result of students along with who got University ranks available in website</label>
                                    <div className="d-flex gap-3">
                                        <label>
                                            <Field type="radio" name="studentResults" value="Yes" className="form-check-input me-1" />
                                            Yes
                                        </label>
                                        <label>
                                            <Field type="radio" name="studentResults" value="No" className="form-check-input me-1" />
                                            No
                                        </label>
                                    </div>
                                    <ErrorMessage name="studentResults" component="div" className="text-danger" />
                                </bie.Col>

                                {/* Extension activities */}
                                <bie.Col xs={12} md={4} lg={3} className="mb-3">
                                    <label>Details of the extension activities carried out in the last academic year available in website</label>
                                    <div className="d-flex gap-3">
                                        <label>
                                            <Field type="radio" name="extensionActivities" value="Yes" className="form-check-input me-1" />
                                            Yes
                                        </label>
                                        <label>
                                            <Field type="radio" name="extensionActivities" value="No" className="form-check-input me-1" />
                                            No
                                        </label>
                                    </div>
                                    <ErrorMessage name="extensionActivities" component="div" className="text-danger" />
                                </bie.Col>
                            </bie.Row>

                            <h4 className="mb-3 innerheader-new pt-3">
                                <span> Society / Trust Details</span>
                            </h4>

                            <bie.Row>
                                {/* Faculty Name */}
                                <bie.Col xs={12} sm={12} md={12} lg={3} xl={3} xxl={3} className="mb-2">
                                    <label>Society / Trust Name </label>
                                    <Field
                                        type="text"
                                        name="societyName"
                                        className="form-control"
                                        // maxLength={40}
                                        disabled
                                    />
                                    {/* <ErrorMessage name="societyName" component="div" className="text-danger" /> */}
                                </bie.Col>

                                <bie.Col xs={12} sm={12} md={12} lg={3} xl={3} xxl={3} className="mb-2">
                                    <label>Society Reg. No. & Date</label>
                                    <Field type="text" name="societyRegNo" className="form-control" maxLength={30} />
                                    <ErrorMessage name="societyRegNo" component="div" className="text-danger" />
                                </bie.Col>

                                <bie.Col xs={12} sm={12} md={12} lg={3} xl={3} xxl={3} className="mb-2">
                                    <label>Secretary / Correspondent Name</label>
                                    <Field
                                        type="text"
                                        name="secretryName"
                                        className="form-control"
                                        disabled
                                    // maxLength={40}
                                    // onKeyPress={allowAlphabetAndSpacesOnly}
                                    />
                                    {/* <ErrorMessage name="secretryName" component="div" className="text-danger" /> */}
                                </bie.Col>

                                <bie.Col xs={12} sm={12} md={12} lg={3} xl={3} xxl={3} className="mb-2">
                                    <label>Secretary Mobile No</label>
                                    <Field type="text" name="secretryMobile" className="form-control" maxLength={10} onKeyPress={allowNumbersOnly} />
                                    <ErrorMessage name="secretryMobile" component="div" className="text-danger" />
                                </bie.Col>

                                <bie.Col xs={12} sm={12} md={12} lg={3} xl={3} xxl={3} className="mb-2">
                                    <label>Secretary e-mail ID</label>
                                    <Field type="email" name="secretryEmail" className="form-control" />
                                    <ErrorMessage name="secretryEmail" component="div" className="text-danger" />
                                </bie.Col>

                                <bie.Col xs={12} sm={12} md={12} lg={3} xl={3} xxl={3} className="mb-2">
                                    <label> Minority Status</label>
                                    <div className="d-flex gap-3">
                                        <label>
                                            <Field type="radio" name="minorStatus" value="YES" className="form-check-input me-1" />
                                            Yes
                                        </label>

                                        <label>
                                            <Field type="radio" name="minorStatus" value="NO" className="form-check-input me-1" />
                                            No
                                        </label>
                                    </div>
                                    <ErrorMessage name="minorStatus" component="div" className="text-danger" />
                                </bie.Col>
                            </bie.Row>

                            <h4 className="mb-3 innerheader-new pt-3">
                                <span> NAAC Details</span>
                            </h4>

                            <bie.Row className="pt-4 m-0 align-items-center">
                                <bie.Col lg={3} className="mb-4">
                                    <label className="mb-0">
                                        <b style={{ color: "red" }}>* </b>
                                        <b>Whether Accredited by NAAC :</b>
                                    </label>
                                </bie.Col>

                                <bie.Col lg={3} className="mb-4">
                                    <Field
                                        as="select"
                                        name="naacAccredited"
                                        className="form-control"
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            InstitutionFormik.setFieldValue("naacAccredited", value);

                                            // RESET ALL
                                            InstitutionFormik.setFieldValue("naacStatus", "");
                                            InstitutionFormik.setFieldValue("latestNaacFromDate", "");
                                            InstitutionFormik.setFieldValue("latestNaacUptoDate", "");
                                            InstitutionFormik.setFieldValue("latestNaacCerti", "");
                                            InstitutionFormik.setFieldValue("latestNaacCgpa", "");
                                            InstitutionFormik.setFieldValue("hasPreviousNaac", "");
                                            InstitutionFormik.setFieldValue("naacCycle", "");
                                            InstitutionFormik.setFieldValue("naacDetails", []);
                                        }}
                                    >
                                        <option value="">--SELECT--</option>
                                        <option value="Yes">Accredited</option>
                                        <option value="A">Applied</option>
                                        <option value="No">Not Yet Applied</option>
                                    </Field>

                                    <ErrorMessage name="naacAccredited" component="div" className="text-danger" />
                                </bie.Col>
                            </bie.Row>
                            {/* ================= Latest NAAC ================= */}
                            {InstitutionFormik.values.naacAccredited === "Yes" && (
                                <div className="border p-3 mt-3 bg-light rounded">
                                    <h6 className="text-primary">Latest NAAC Details</h6>

                                    <div className="row">
                                        <div className="col-md-3">
                                            <label>
                                                <b style={{ color: "red" }}>* </b> Grade
                                            </label>
                                            <Field as="select" name="naacStatus" className="form-control">
                                                <option value="">--SELECT--</option>
                                                <option value="A">A</option>
                                                <option value="A+">A+</option>
                                                <option value="A++">A++</option>
                                                <option value="B">B</option>
                                                <option value="B+">B+</option>
                                                <option value="B++">B++</option>
                                                <option value="C">C</option>
                                                <option value="D">D</option>
                                            </Field>
                                            <ErrorMessage name="naacStatus" component="div" className="text-danger" />
                                        </div>
                                        <div className="col-md-3">
                                            <label>
                                                <b style={{ color: "red" }}>* </b>CGPA
                                            </label>
                                            <Field type="text" name="latestNaacCgpa" className="form-control" />
                                            <ErrorMessage name="latestNaacCgpa" component="div" className="text-danger" />
                                        </div>

                                        <div className="col-md-3">
                                            <label>
                                                <b style={{ color: "red" }}>* </b> From Date
                                            </label>
                                            <Field type="date" name="latestNaacFromDate" className="form-control" />
                                            <ErrorMessage name="latestNaacFromDate" component="div" className="text-danger" />
                                        </div>

                                        <div className="col-md-3">
                                            <label>
                                                <b style={{ color: "red" }}>* </b> To Date
                                            </label>
                                            <Field type="date" name="latestNaacUptoDate" className="form-control" />
                                            <ErrorMessage name="latestNaacUptoDate" component="div" className="text-danger" />
                                        </div>

                                        <div className="col-md-4 mt-2">
                                            <label>
                                                {" "}
                                                <b style={{ color: "red" }}>* </b>Upload Certificate
                                            </label>
                                            <input
                                                type="file"
                                                className="form-control"
                                                onChange={(e) => {
                                                    let filename = `latestNaacCerti`;
                                                    let path = "/Uploads/AffiliationPDFs/2023/";

                                                    ImagePdfBucket(e, InstitutionFormik, path, `latestNaacCerti`, filename, "pdf");
                                                }}
                                            />
                                            {InstitutionFormik?.values?.latestNaacCerti && !["null", "nullnull", ""].includes(InstitutionFormik.values.latestNaacCerti) && (
                                                <button type="button" className="btn btn-primary" onClick={() => viewImage(InstitutionFormik.values.latestNaacCerti)}>
                                                    <FaEye size={18} />
                                                </button>
                                            )}
                                            <ErrorMessage name="latestNaacCerti" component="div" className="text-danger" />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ================= Previous NAAC ================= */}
                            {InstitutionFormik.values.naacAccredited === "Yes" && (
                                <>
                                    <div className="mt-4 d-flex align-items-center">
                                        <label className="me-3 mb-0">
                                            <b style={{ color: "red" }}>* </b> Previous NAAC Cycle's Available?
                                        </label>

                                        <label className="me-3 mb-0">
                                            <Field
                                                type="radio"
                                                name="hasPreviousNaac"
                                                value="Yes"
                                                onChange={() => {
                                                    InstitutionFormik.setFieldValue("hasPreviousNaac", "Yes");
                                                    InstitutionFormik.setFieldValue("naacCycle", "");
                                                    InstitutionFormik.setFieldValue("naacDetails", []);
                                                }}
                                            />{" "}
                                            Yes
                                        </label>

                                        <label className="mb-0">
                                            <Field
                                                type="radio"
                                                name="hasPreviousNaac"
                                                value="No"
                                                onChange={() => {
                                                    InstitutionFormik.setFieldValue("hasPreviousNaac", "No");
                                                    InstitutionFormik.setFieldValue("naacCycle", "");
                                                    InstitutionFormik.setFieldValue("naacDetails", []);
                                                }}
                                            />{" "}
                                            No
                                        </label>
                                    </div>
                                    <ErrorMessage name="hasPreviousNaac" component="div" className="text-danger ms-2" />
                                </>
                            )}

                            {/* ================= Cycle ================= */}
                            {InstitutionFormik.values.hasPreviousNaac === "Yes" && (
                                <>
                                    <div className="mt-3 d-flex align-items-center">
                                        <label className="me-2 mb-0" style={{ minWidth: "120px" }}>
                                            <b style={{ color: "red" }}>* </b>NAAC Cycle
                                        </label>

                                        <Field
                                            as="select"
                                            name="naacCycle"
                                            className="form-control"
                                            onChange={(e) => {
                                                const count = parseInt(e.target.value || 0);

                                                InstitutionFormik.setFieldValue("naacCycle", e.target.value);

                                                if (count > 0) {
                                                    const arr = Array.from({ length: count }, () => ({
                                                        naacFromDate: "",
                                                        naacUptoDate: "",
                                                        naacAccredCerti: "",
                                                        naacCgpa: "",
                                                        naacStatus: "",
                                                    }));
                                                    InstitutionFormik.setFieldValue("naacDetails", arr);
                                                } else {
                                                    InstitutionFormik.setFieldValue("naacDetails", []);
                                                }
                                            }}
                                            style={{ maxWidth: "200px" }}
                                        >
                                            <option value="">--SELECT--</option>
                                            {[1, 2, 3, 4, 5, 6].map((n) => (
                                                <option key={n} value={n}>
                                                    {n}
                                                </option>
                                            ))}
                                        </Field>
                                    </div>
                                    <ErrorMessage name="naacCycle" component="div" className="text-danger ms-2" />
                                </>
                            )}

                            {/* ================= FieldArray ================= */}
                            {InstitutionFormik.values.hasPreviousNaac === "Yes" && InstitutionFormik?.values?.naacCycle && (
                                <FieldArray name="naacDetails">
                                    {() =>
                                        InstitutionFormik?.values?.naacDetails?.map((item, index) => (
                                            <div
                                                key={index}
                                                className="mt-4 p-3 rounded shadow-sm"
                                                style={{
                                                    borderLeft: "4px solid #0d6efd",
                                                    background: "#f8f9fa",
                                                }}
                                            >
                                                <h6 className="text-primary">Previous Cycle {index + 1}</h6>

                                                <div className="row">
                                                    <div className="col-md-3">
                                                        <label>
                                                            <b style={{ color: "red" }}>* </b>NAAC Grade
                                                        </label>
                                                        <Field as="select" className="form-control" name={`naacDetails[${index}].naacStatus`}>
                                                            <option value="">--SELECT--</option>
                                                            <option value="A">A</option>
                                                            <option value="A+">A+</option>
                                                            <option value="A++">A++</option>
                                                            <option value="B">B</option>
                                                            <option value="B+">B+</option>
                                                            <option value="B++">B++</option>
                                                            <option value="C">C</option>
                                                            <option value="D">D</option>
                                                        </Field>
                                                        <ErrorMessage name={`naacDetails[${index}].naacStatus`} component="div" className="text-danger" />
                                                    </div>

                                                    <div className="col-md-3">
                                                        <label>
                                                            <b style={{ color: "red" }}>* </b>NAAC CGPA
                                                        </label>
                                                        <Field type="text" name={`naacDetails[${index}].naacCgpa`} className="form-control" />
                                                        <ErrorMessage name={`naacDetails[${index}].naacCgpa`} component="div" className="text-danger" />
                                                    </div>
                                                    <div className="col-md-3">
                                                        <label>
                                                            <b style={{ color: "red" }}>* </b> From Date
                                                        </label>
                                                        <Field type="date" name={`naacDetails[${index}].naacFromDate`} className="form-control" />
                                                        <ErrorMessage name={`naacDetails[${index}].naacFromDate`} component="div" className="text-danger" />
                                                    </div>

                                                    <div className="col-md-3">
                                                        <label>
                                                            <b style={{ color: "red" }}>* </b> To Date
                                                        </label>
                                                        <Field type="date" name={`naacDetails[${index}].naacUptoDate`} className="form-control" />
                                                        <ErrorMessage name={`naacDetails[${index}].naacUptoDate`} component="div" className="text-danger" />
                                                    </div>

                                                    <div className="col-md-3">
                                                        <label>
                                                            <b style={{ color: "red" }}>* </b>Upload Certificate
                                                        </label>
                                                        <input
                                                            type="file"
                                                            className="form-control"
                                                            name={`naacDetails[${index}].naacAccredCerti`}
                                                            onChange={(e) => {
                                                                let filename = `naacAccredCerti_${index}`;
                                                                let path = "/Uploads/AffiliationPDFs/2023/";

                                                                ImagePdfBucket(e, InstitutionFormik, path, `naacDetails[${index}].naacAccredCerti`, filename, "pdf");
                                                            }}
                                                        />

                                                        {InstitutionFormik?.values?.naacDetails?.[index]?.naacAccredCerti && !["null", "nullnull", ""].includes(InstitutionFormik?.values?.naacDetails[index]?.naacAccredCerti) && (
                                                            <button type="button" className="btn btn-primary" onClick={() => viewImage(InstitutionFormik?.values?.naacDetails[index]?.naacAccredCerti)}>
                                                                <FaEye size={18} />
                                                            </button>
                                                        )}
                                                        <ErrorMessage name={`naacDetails[${index}].naacAccredCerti`} component="div" className="text-danger" />
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    }
                                </FieldArray>
                            )}


                            <div style={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
                                {paymentDone ? (
                                    <div style={{ color: "green", fontWeight: "bold" }}>
                                        ✅ Already submitted
                                    </div>
                                ) : (
                                    <button
                                        type="submit"
                                        className="btn btn-success btn-sm"
                                    >
                                        Submit
                                    </button>
                                )}
                            </div>



                        </Form>
                    </FormikProvider>
                </div>
            </div>
        </>
    );
}
